import React, { useEffect, useState } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Alert, Switch } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import { getDisplayName, setDisplayName, changePassword, resync, localpart } from '../matrix';
import { usePrefs, setPrefs, ThemePref } from '../prefs';
import { HS_LABEL } from '../config';
import { Btn } from '../ui/Btn';
import { KeyboardShift } from '../ui/KeyboardShift';
import { radius, makeStyles, useTheme, webSwitch } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Settings'>;
// eslint-disable-next-line @typescript-eslint/no-var-requires
const APP_VERSION: string = require('../../app.json').expo.version;
// Which code this phone runs: the OTA update id + publish time, or "dev" under Metro / Expo Go.
const CODE_LABEL = (() => {
  try {
    const u = require('expo-updates');
    if (u.updateId) return `update ${String(u.updateId).slice(0, 8)}${u.createdAt ? ` · ${new Date(u.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`;
    if (u.isEmbeddedLaunch) return 'built-in (no update yet)';
  } catch {}
  return 'dev server';
})();
const ARCHIVE_CHOICES = [7, 14, 30, 90];
const THEME_CHOICES: { key: ThemePref; label: string; hint: string }[] = [
  { key: 'dark', label: 'Dark', hint: 'The Helm look' },
  { key: 'light', label: 'Light', hint: 'Bright and paper-like' },
  { key: 'system', label: 'System', hint: 'Follows the phone setting' },
];

export default function SettingsScreen({ navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const { userId, logout } = useAuth();
  const prefs = usePrefs();
  const [name, setName] = useState('');
  const [nameLoaded, setNameLoaded] = useState(false);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => { getDisplayName().then((n) => { setName(n); setNameLoaded(true); }); }, []);

  const run = async (key: string, fn: () => Promise<any>, ok: string) => {
    setBusy(key); setNote(null);
    try { await fn(); setNote(ok); }
    catch (e: any) { Alert.alert('That didn’t work', e?.message || 'Unknown error'); }
    finally { setBusy(null); }
  };

  const saveName = () => { if (!name.trim()) return; run('name', () => setDisplayName(name), 'Display name updated'); };
  const savePassword = () => {
    if (!cur || !next) return;
    if (next.length < 8) { Alert.alert('Too short', 'Use at least 8 characters.'); return; }
    if (next !== confirm) { Alert.alert('Passwords don’t match', 'Type the new password the same way twice.'); return; }
    run('pw', async () => { await changePassword(cur, next); setCur(''); setNext(''); setConfirm(''); }, 'Password changed. You stay signed in on this phone.');
  };
  const refresh = () => run('resync', () => resync(), 'Re-downloading everything from the server…');
  const signOut = () => Alert.alert('Sign out?', undefined, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Sign out', style: 'destructive', onPress: () => logout() },
  ]);

  return (
    <KeyboardShift>
    <ScrollView style={s.root} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
      {note ? <Text style={s.note}>{note}</Text> : null}

      <View style={s.card}>
        <Text style={s.h}>Inbox</Text>
        <View style={s.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchLabel}>Auto-archive quiet rooms</Text>
            <Text style={s.switchSub}>{prefs.archiveDays > 0 ? `No messages for ${prefs.archiveDays} days → moves to Archived` : 'Off — rooms stay listed until you hide them'}</Text>
          </View>
          <Switch {...webSwitch} value={prefs.archiveDays > 0} onValueChange={(v) => setPrefs({ archiveDays: v ? 7 : 0 })} trackColor={{ true: theme.accent, false: theme.surface2 }} thumbColor="#fff" />
        </View>
        {prefs.archiveDays > 0 && (
          <View style={s.chips}>
            {ARCHIVE_CHOICES.map((d) => {
              const on = prefs.archiveDays === d;
              return (
                <Pressable key={d} onPress={() => setPrefs({ archiveDays: d })} style={[s.chip, on && s.chipOn]} accessibilityRole="button" accessibilityState={{ selected: on }}>
                  <Text style={[s.chipText, on && s.chipTextOn]}>{d} days</Text>
                </Pressable>
              );
            })}
          </View>
        )}
        <Text style={s.hint}>Rooms inside a workspace never auto-archive. Archive any room yourself by swiping it or from its long-press menu; archived rooms stay one tap away at the bottom of Home.</Text>
        <View style={[s.switchRow, { marginTop: 4 }]}>
          <View style={{ flex: 1 }}>
            <Text style={s.switchLabel}>Mark people with an H badge</Text>
            <Text style={s.switchSub}>Bots always carry a B badge on their picture</Text>
          </View>
          <Switch {...webSwitch} value={prefs.humanBadge} onValueChange={(v) => setPrefs({ humanBadge: v })} trackColor={{ true: theme.accent, false: theme.surface2 }} thumbColor="#fff" />
        </View>
      </View>

      <View style={s.card}>
        <Text style={s.h}>Appearance</Text>
        <View style={s.chips}>
          {THEME_CHOICES.map((c) => {
            const on = prefs.theme === c.key;
            return (
              <Pressable key={c.key} onPress={() => setPrefs({ theme: c.key })} style={[s.chip, on && s.chipOn]} accessibilityRole="button" accessibilityState={{ selected: on }}>
                <Text style={[s.chipText, on && s.chipTextOn]}>{c.label}</Text>
              </Pressable>
            );
          })}
        </View>
        <Text style={s.hint}>{THEME_CHOICES.find((c) => c.key === prefs.theme)?.hint}. Applies instantly, everywhere.</Text>
      </View>

      <View style={s.card}>
        <Text style={s.h}>Account</Text>
        <Row k="Signed in as" v={userId ? localpart(userId) : ''} />
        <Row k="Matrix ID" v={userId || ''} />
        <Text style={s.label}>Display name</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TextInput style={[s.input, { flex: 1 }]} value={name} onChangeText={setName} placeholder={nameLoaded ? 'Your name' : 'Loading…'}
            placeholderTextColor={theme.faint} autoCorrect={false} onSubmitEditing={saveName} />
          <Btn label="Save" onPress={saveName} busy={busy === 'name'} />
        </View>
      </View>

      <View style={s.card}>
        <Text style={s.h}>Change password</Text>
        <Text style={s.hint}>Other devices stay signed in. Use at least 8 characters.</Text>
        <TextInput style={s.input} value={cur} onChangeText={setCur} placeholder="Current password" placeholderTextColor={theme.faint} secureTextEntry textContentType="password" />
        <TextInput style={[s.input, { marginTop: 8 }]} value={next} onChangeText={setNext} placeholder="New password" placeholderTextColor={theme.faint} secureTextEntry textContentType="newPassword" />
        <TextInput style={[s.input, { marginTop: 8 }]} value={confirm} onChangeText={setConfirm} placeholder="New password again" placeholderTextColor={theme.faint} secureTextEntry textContentType="newPassword" onSubmitEditing={savePassword} />
        <View style={{ marginTop: 10 }}><Btn label="Change password" onPress={savePassword} busy={busy === 'pw'} /></View>
      </View>

      <View style={s.card}>
        <Text style={s.h}>Data</Text>
        <Text style={s.hint}>The app keeps a local copy of your rooms so it opens instantly. If something looks stale, refresh it.</Text>
        <Btn label="Refresh from server" kind="ghost" onPress={refresh} busy={busy === 'resync'} />
      </View>

      <View style={s.card}>
        <Text style={s.h}>About</Text>
        <Row k="App" v={`Helm ${APP_VERSION}`} />
        <Row k="Code" v={CODE_LABEL} />
        <Row k="Homeserver" v={HS_LABEL} />
        <Row k="Protocol" v="Matrix" />
      </View>

      <View style={[s.card, { alignItems: 'stretch' }]}>
        <Btn label="Sign out" kind="danger" onPress={signOut} />
      </View>
      <Pressable onPress={() => navigation.goBack()} style={{ alignSelf: 'center', marginTop: 8 }}><Text style={s.back}>Done</Text></Pressable>
    </ScrollView>
    </KeyboardShift>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  const theme = useTheme(); const s = useStyles();
  return <View style={s.kv}><Text style={s.k}>{k}</Text><Text style={s.v} numberOfLines={1}>{v}</Text></View>;
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  note: { color: theme.good, fontSize: 13, marginBottom: 8, fontWeight: '600' },
  card: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 14, marginBottom: 14 },
  h: { color: theme.faint, fontSize: 11, fontWeight: '800', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10, fontFamily: theme.mono },
  hint: { color: theme.muted, fontSize: 13, marginBottom: 10, lineHeight: 18 },
  label: { color: theme.muted, fontSize: 12, fontWeight: '700', marginTop: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  kv: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 4 },
  k: { color: theme.muted, fontSize: 14 },
  v: { color: theme.ink, fontSize: 14, fontWeight: '600', flexShrink: 1, fontFamily: theme.mono },
  input: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, color: theme.ink, fontSize: 15, paddingHorizontal: 12, paddingVertical: 10 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 4, marginBottom: 6 },
  switchLabel: { color: theme.ink, fontSize: 15, fontWeight: '700' },
  switchSub: { color: theme.faint, fontSize: 12, marginTop: 2 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  chip: { borderRadius: radius.pill, paddingHorizontal: 12, paddingVertical: 7, backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border },
  chipOn: { backgroundColor: theme.accentBg, borderColor: theme.accent },
  chipText: { color: theme.muted, fontSize: 13, fontWeight: '700', fontFamily: theme.mono },
  chipTextOn: { color: theme.accent },
  back: { color: theme.accent, fontSize: 15, fontWeight: '600' },
}));
